import React from "react";
import { FiShield } from "react-icons/fi";

import PageAvatar from "../PageAvatar";

type Props = {
  name?: string;
  logo?: string;
  tagline?: string;
  /** Compact variant for pages where the payment card is the main event. */
  size?: "lg" | "sm";
  showSecureBadge?: boolean;
};

/**
 * The merchant's identity at the top of every customer-facing page. Logo first,
 * name second — the person paying should recognise who they are paying before
 * anything else on the screen, which is the whole trust argument for these
 * pages. Deexoptions branding stays out of the way down in the footer.
 */
const BusinessBrandHeader = ({
  name,
  logo,
  tagline,
  size = "lg",
  showSecureBadge = true,
}: Props) => {
  const large = size === "lg";

  return (
    <div className="flex flex-col items-center text-center">
      <PageAvatar
        name={name}
        image={logo}
        className={
          large
            ? "size-[88px] shadow-[0_8px_24px_-8px_rgba(16,24,40,0.25)] ring-4 ring-white"
            : "size-[64px] shadow-[0_6px_18px_-8px_rgba(16,24,40,0.25)] ring-4 ring-white"
        }
        textClassName={large ? "text-[32px]" : "text-[24px]"}
      />

      <h1
        className={`mt-4 font-sora font-bold tracking-[-0.01em] text-gray-900 ${
          large ? "text-[22px] mds:text-[26px]" : "text-[18px]"
        }`}
      >
        {name || "Payment"}
      </h1>

      {tagline && (
        <p className="mt-1.5 max-w-[380px] text-[13.5px] font-medium leading-relaxed text-gray-500">
          {tagline}
        </p>
      )}

      {showSecureBadge && (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 ring-1 ring-gray-200/80">
          <FiShield className="text-[12px] text-emerald-500" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500">
            Secure payment
          </span>
        </div>
      )}
    </div>
  );
};

export default BusinessBrandHeader;
